// apps/nos/client/src/services/instruments_api.ts
//
// Axios bindings for the /me/instruments endpoints. Server-side spec:
// apps/nos/docs/superpowers/specs/2026-06-04-manage-chromatography-design.md
import axios from "axios";
import { serverURL } from "@/utils/serverURL";

// withCredentials so the browser sends Clerk session cookies on every call.
// Mirrors apps/nos/client/src/services/inventory_api.ts. Without this the
// Clerk requireAuth() middleware sees an unauthenticated request and
// 302-redirects to /, which axios then resolves as success with HTML body
// — surfacing as "Cannot read properties of undefined" downstream.
const client = axios.create({ withCredentials: true });

// ─── Catalogs (read-only nfood projections) ───────────────────────────

export type TypeInstrumentRow = {
  id: number;
  instrument_name: string;
  instrument_family: string;
  instrument_provider: string | null;
  analyser_type: string | null;
  resolution: string;
};

export type TypeColumnRow = {
  id: number;
  column_manufacturer: string;
  column_product_name: string;
  polarity: string;
  agilent_equivalent: string;
  stationary_phase_composition: unknown;
};

export type CatalogsResponse = {
  instruments: TypeInstrumentRow[];
  columns: TypeColumnRow[];
  vendors: Array<{ id: string; label: string }>;
  analyser_types: string[];
};

export async function fetchCatalogs(): Promise<CatalogsResponse> {
  const { data } = await client.get<CatalogsResponse>(serverURL`/me/instruments/catalogs`);
  return data;
}

// ─── Configs ──────────────────────────────────────────────────────────

export type ConfigDTO = {
  id: string;
  name: string;
  notes: string | null;
  type_instrument_id: number;
  type_column_id: number;
  ionisation_energy_level: number;
  methodology: Record<string, unknown>;
  scope: "org" | "personal";
  organization_id: string;
  user_profile_id: string | null;
  current_nfood_experiment_information_id: number | null;
  tag_ids: string[];
  ladder_count: number;
  chromatogram_count: number;
  created_at: string;
  created_by_profile: string;
  updated_at: string;
  archived_at: string | null;
};

export type ConfigCreateInput = {
  name: string;
  notes?: string | null;
  type_instrument_id: number;
  type_column_id: number;
  ionisation_energy_level: number;
  methodology: Record<string, unknown>;
  tag_ids?: string[];
  // Draft-scope alkane ladder file_uploads to claim onto the new config
  // after nfood register succeeds. Each id refers to a file_upload row
  // created via POST /me/instruments/ladders/presign.
  alkane_ladder_draft_file_upload_ids?: string[];
};

export type ConfigUpdateInput = Partial<ConfigCreateInput>;

export async function listConfigs(): Promise<ConfigDTO[]> {
  const { data } = await client.get<ConfigDTO[]>(serverURL`/me/instruments/configs`);
  return data;
}

export async function createConfig(payload: ConfigCreateInput) {
  const { data } = await client.post(serverURL`/me/instruments/configs`, payload);
  return data as {
    id: string;
    current_nfood_experiment_information_id: number;
    row: unknown;
  };
}

export async function patchConfig(id: string, patch: ConfigUpdateInput) {
  const { data } = await client.patch(serverURL`/me/instruments/configs/${id}`, patch);
  return data as
    | { id: string; autosave: true }
    | { id: string; current_nfood_experiment_information_id: number; rotated: boolean }
    | { id: string; no_changes: true };
}

export async function archiveConfig(id: string): Promise<void> {
  await client.delete(serverURL`/me/instruments/configs/${id}`);
}

// ─── Default ──────────────────────────────────────────────────────────

export async function getDefault(): Promise<{ user_exp_config_id: string | null }> {
  const { data } = await client.get(serverURL`/me/instruments/default`);
  return data;
}

export async function setDefault(user_exp_config_id: string) {
  const { data } = await client.put(serverURL`/me/instruments/default`, {
    user_exp_config_id,
  });
  return data;
}

export async function clearDefault(): Promise<void> {
  await client.delete(serverURL`/me/instruments/default`);
}

// ─── Tags ─────────────────────────────────────────────────────────────

export type TagDTO = {
  id: string;
  display_text: string;
  scope: "org" | "personal";
};

export async function listTags(): Promise<TagDTO[]> {
  const { data } = await client.get<TagDTO[]>(serverURL`/me/instruments/tags`);
  return data;
}

export async function createTag(display_text: string) {
  const { data } = await client.post(serverURL`/me/instruments/tags`, { display_text });
  return data as TagDTO & { existed: boolean };
}

export async function renameTag(tagId: string, display_text: string) {
  const { data } = await client.patch(
    serverURL`/me/instruments/tags/${tagId}`,
    { display_text },
  );
  return data as { id: string; display_text: string };
}

export async function archiveTag(tagId: string): Promise<void> {
  await client.delete(serverURL`/me/instruments/tags/${tagId}`);
}

export async function attachTag(configId: string, tagId: string) {
  const { data } = await client.post(
    serverURL`/me/instruments/configs/${configId}/tags/${tagId}`,
  );
  return data as { rel_id: string; existed?: boolean; revived?: boolean };
}

export async function detachTag(configId: string, tagId: string): Promise<void> {
  await client.delete(serverURL`/me/instruments/configs/${configId}/tags/${tagId}`);
}

// ─── Alkane ladders ───────────────────────────────────────────────────

export type LadderRow = {
  ladder_id: string;
  file_upload_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  etag: string | null;
  uploaded_at: string | null;
  created_at: string;
};

export async function listConfigLadders(configId: string): Promise<LadderRow[]> {
  const { data } = await client.get<LadderRow[]>(
    serverURL`/me/instruments/configs/${configId}/ladders`,
  );
  return data;
}

export type LadderPresignInput = {
  filename: string;
  content_type: string;
  size_bytes: number;
};

export type LadderPresignResponse = {
  file_upload_id: string;
  presigned_url: string;
  s3_bucket: string;
  s3_key: string;
  expires_at: string;
  required_headers: Record<string, string>;
};

export async function presignLadder(
  configId: string,
  payload: LadderPresignInput,
): Promise<LadderPresignResponse> {
  const { data } = await client.post(
    serverURL`/me/instruments/configs/${configId}/ladders/presign`,
    payload,
  );
  return data;
}

export async function completeLadder(configId: string, fileUploadId: string) {
  const { data } = await client.post(
    serverURL`/me/instruments/configs/${configId}/ladders/${fileUploadId}/complete`,
  );
  return data as {
    ladder_id: string;
    file_upload: { file_upload_id: string; status: "uploaded"; etag: string | null | undefined; uploaded_at: string };
  };
}

export async function archiveLadder(configId: string, ladderId: string): Promise<void> {
  await client.delete(
    serverURL`/me/instruments/configs/${configId}/ladders/${ladderId}`,
  );
}

// ─── Draft-scope alkane ladders (pre-register) ───────────────────────
// Used by InstrumentDrawer's "new" mode so the user can upload ladders
// before the user_exp_config row exists. Claimed onto the config in
// the create handler's Stage 3b once nfood returns successfully.

export type DraftLadderPresignInput = {
  draft_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
};

export async function presignDraftLadder(
  payload: DraftLadderPresignInput,
): Promise<LadderPresignResponse> {
  const { data } = await client.post(
    serverURL`/me/instruments/ladders/presign`,
    payload,
  );
  return data;
}

export async function completeDraftLadder(fileUploadId: string) {
  const { data } = await client.post(
    serverURL`/me/instruments/ladders/${fileUploadId}/complete`,
  );
  return data as {
    file_upload_id: string;
    status: "uploaded";
    etag: string | null;
    uploaded_at: string;
  };
}

export async function abandonDraftLadder(fileUploadId: string): Promise<void> {
  await client.delete(serverURL`/me/instruments/ladders/${fileUploadId}`);
}

export async function abandonDraftLadders(
  draftId: string,
): Promise<{ abandoned_count: number }> {
  const { data } = await client.delete(
    serverURL`/me/instruments/ladders/drafts/${draftId}`,
  );
  return data;
}

// ─── Chromatograms ────────────────────────────────────────────────────

export type OrphanChromatogram = {
  file_upload_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string | null;
  metadata: Record<string, unknown>;
};

export type ConfigChromatogramRow = {
  chromatogram_id: string;
  file_upload_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string | null;
  created_at: string;
};

export async function listConfigChromatograms(
  configId: string,
): Promise<ConfigChromatogramRow[]> {
  const { data } = await client.get<ConfigChromatogramRow[]>(
    serverURL`/me/instruments/configs/${configId}/chromatograms`,
  );
  return data;
}

export async function listOrphans(): Promise<OrphanChromatogram[]> {
  const { data } = await client.get<OrphanChromatogram[]>(
    serverURL`/me/instruments/chromatograms/orphans`,
  );
  return data;
}

export async function assignChromatogram(configId: string, fileUploadId: string) {
  const { data } = await client.post(
    serverURL`/me/instruments/configs/${configId}/chromatograms/${fileUploadId}`,
  );
  return data as { chromatogram_id: string };
}

export async function archiveChromatogram(chromatogramId: string): Promise<void> {
  await client.delete(serverURL`/me/instruments/chromatograms/${chromatogramId}`);
}

// ─── Inventory-scoped chromatograms ────────────────────────────────────
//
// Server endpoints under /me/inventory/:id/chromatograms — used by the
// Substance Library drawer (mixture inventory items only).

export type InventoryChromatogramRow = {
  state: "attached" | "pending";
  inventory_chromatogram_id: string | null;
  chromatogram_id: string | null;
  user_exp_config_id: string | null;
  file_upload_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string | null;
  created_at: string;
};

export async function listInventoryChromatograms(
  inventoryId: string,
): Promise<InventoryChromatogramRow[]> {
  const { data } = await client.get<InventoryChromatogramRow[]>(
    serverURL`/me/inventory/${inventoryId}/chromatograms`,
  );
  return data;
}

export type InventoryChromaPresignInput = {
  filename: string;
  content_type: string;
  size_bytes: number;
};
export type InventoryChromaPresignResponse = {
  file_upload_id: string;
  presigned_url: string;
  s3_bucket: string;
  s3_key: string;
  expires_at: string;
  required_headers: Record<string, string>;
};

export async function presignInventoryChromatogram(
  inventoryId: string,
  payload: InventoryChromaPresignInput,
): Promise<InventoryChromaPresignResponse> {
  const { data } = await client.post(
    serverURL`/me/inventory/${inventoryId}/chromatograms/presign`,
    payload,
  );
  return data;
}

export async function completeInventoryChromatogram(
  inventoryId: string,
  fileUploadId: string,
) {
  const { data } = await client.post(
    serverURL`/me/inventory/${inventoryId}/chromatograms/${fileUploadId}/complete`,
  );
  return data;
}

export async function attachInventoryChromatogram(
  inventoryId: string,
  fileUploadId: string,
  userExpConfigId: string,
) {
  const { data } = await client.post(
    serverURL`/me/inventory/${inventoryId}/chromatograms/${fileUploadId}/attach`,
    { user_exp_config_id: userExpConfigId },
  );
  return data as { chromatogram_id: string; inventory_chromatogram_id: string };
}

export async function changeInventoryChromaConfig(
  inventoryId: string,
  chromatogramId: string,
  userExpConfigId: string,
) {
  const { data } = await client.patch(
    serverURL`/me/inventory/${inventoryId}/chromatograms/${chromatogramId}/config`,
    { user_exp_config_id: userExpConfigId },
  );
  return data as { chromatogram_id: string; user_exp_config_id: string };
}

export async function archiveInventoryChromatogram(
  inventoryId: string,
  idOrFileUploadId: string,
): Promise<void> {
  await client.delete(
    serverURL`/me/inventory/${inventoryId}/chromatograms/${idOrFileUploadId}`,
  );
}

// ─── Draft-scope inventory chromatograms ──────────────────────────────
//
// Server endpoints under /me/inventory/chromatograms/* — used by the
// bulk-upload pipeline (F16) to presign, complete, abandon, and finalize
// draft chromatogram uploads before they are attached to an inventory item.

export type DraftPresignInput = {
  inventory_draft_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
};
export type DraftPresignResponse = {
  file_upload_id: string;
  presigned_url: string;
  s3_bucket: string;
  s3_key: string;
  expires_at: string;
  required_headers: Record<string, string>;
};

export async function presignDraftInventoryChromatogram(
  payload: DraftPresignInput,
): Promise<DraftPresignResponse> {
  const { data } = await client.post(
    serverURL`/me/inventory/chromatograms/presign`,
    payload,
  );
  return data;
}

export async function completeDraftInventoryChromatogram(fileUploadId: string) {
  const { data } = await client.post(
    serverURL`/me/inventory/chromatograms/${fileUploadId}/complete`,
  );
  return data as {
    file_upload_id: string;
    status: "uploaded";
    etag: string | null | undefined;
    uploaded_at: string;
  };
}

export async function abandonDraftInventoryChromatogram(
  fileUploadId: string,
): Promise<void> {
  await client.delete(
    serverURL`/me/inventory/chromatograms/draft-upload/${fileUploadId}`,
  );
}

export async function abandonDraftInventoryChromas(
  inventoryDraftId: string,
): Promise<{ abandoned_count: number }> {
  const { data } = await client.delete(
    serverURL`/me/inventory/chromatograms/draft/${inventoryDraftId}`,
  );
  return data;
}

export type FinalizeInventoryChromaAssignment = {
  file_upload_id: string;
  user_exp_config_id: string;
};
export type FinalizeInventoryChromasInput = {
  inventory_draft_id: string;
  assignments: FinalizeInventoryChromaAssignment[];
};
export type FinalizeInventoryChromasResponse = {
  results: Array<{
    file_upload_id: string;
    chromatogram_id: string;
    inventory_chromatogram_id: string;
  }>;
};

export async function finalizeInventoryChromatograms(
  inventoryId: string,
  payload: FinalizeInventoryChromasInput,
): Promise<FinalizeInventoryChromasResponse> {
  const { data } = await client.post(
    serverURL`/me/inventory/${inventoryId}/chromatograms/finalize`,
    payload,
  );
  return data;
}
